import * as React from 'react';
import type { Reducer } from 'react';

const SelectedRowsContext = React.createContext(null);
const SelectedRowsDispatchContext = React.createContext(null);

export type SelectedRowsAction<T> =
  | { type: 'update'; payload: T; key?: string }
  | { type: 'add'; payload: T[]; key?: string }
  | { type: 'remove'; payload: T[]; key?: string }
  | { type: 'clear' };

type BaseStateType = {
  id?: string;
  pack_id?: string;
};

function selectedRowsReducer<T extends BaseStateType>(state: T[], action: SelectedRowsAction<T>) {
  const uniqueIdKey = action.type !== 'clear' ? action.key || 'id' : undefined;

  switch (action.type) {
    case 'update':
      return state.map((stateRow: T) => {
        if (stateRow[uniqueIdKey] === action.payload[uniqueIdKey]) return action.payload;

        return stateRow;
      });
    case 'add':
      return [
        ...state,
        ...action.payload.filter(
          (payloadRow: T) => !state.find((stateRow: T) => stateRow[uniqueIdKey] === payloadRow[uniqueIdKey]),
        ),
      ];
    case 'remove':
      return state.filter(
        (stateRow: T) => !action.payload.find((payloadRow: T) => stateRow[uniqueIdKey] === payloadRow[uniqueIdKey]),
      );
    case 'clear':
      return [];
    default:
      return state;
  }
}

type ProviderProps = {
  children: React.ReactNode;
};

export function SelectedRowsProvider<T extends BaseStateType>({ children }: ProviderProps) {
  const [state, dispatch] = React.useReducer<Reducer<T[], SelectedRowsAction<T>>>(selectedRowsReducer, []);

  return (
    <SelectedRowsContext.Provider value={state}>
      <SelectedRowsDispatchContext.Provider value={dispatch}>{children}</SelectedRowsDispatchContext.Provider>
    </SelectedRowsContext.Provider>
  );
}

export function useSelectedRows() {
  return React.useContext(SelectedRowsContext);
}

export function useSelectedRowsDispatch() {
  return React.useContext(SelectedRowsDispatchContext);
}
